import React, {useState, useCallback} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { summaryReportActions } from '../store';
import {DataGrid, GridOverlay} from '@material-ui/data-grid';
import { Grid, FormControlLabel, Checkbox, makeStyles, Paper } from '@material-ui/core';
import { useTheme } from "@material-ui/core/styles";
import {
  formatDistance,
  formatHours,
  formatDate,
  formatSpeed,
  formatVolume,
  formatAgt,
  formatPosition
} from '../common/formatter';
import ReportFilter from './ReportFilter';
import ReportLayoutPage from './ReportLayoutPage';
import { useAttributePreference } from '../common/preferences';
import { t } from '../common/LocalizationProvider';
import {cyberStyles} from "../cssShared/cyberTheme";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";

const useStyles = makeStyles((theme) => ({
  ...cyberStyles(theme),
}));

const Filter = ({ loading, setLoading }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [daily, setDaily] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(null)

  const handleSubmit = useCallback( async (deviceIds, from, to, mail, headers) => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let query = new URLSearchParams({ from, to, mail });
    deviceIds.forEach(deviceId => query.append("deviceId", deviceId))
    if (headers.Accept !== 'application/json')
      query.append("timeZone", timeZone)

    const response = await fetch(`/api/reports/summary?${query.toString()}`, { headers });
    if (response.ok) {
      if (mail)
        setOpenSnackbar('EmailReportAlert')
      const contentType = response.headers.get('content-type');
      if (contentType) {
        if (contentType === 'application/json') {
          const res = await response.json()
          dispatch(summaryReportActions.setItems(res));
          if (res.length === 0)
            setOpenSnackbar('replayNoData')
        } else {
          window.location.assign(window.URL.createObjectURL(await response.blob()));
          setOpenSnackbar('ExcelFileExported')
        }
      }
    }
    else
      setOpenSnackbar('UnknownProblem')
    setLoading(null)
  },[dispatch, daily])

  return (
      <ReportFilter handleSubmit={handleSubmit} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} loading={loading} setLoading={setLoading}>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
              className={classes.checkboxLabel}
              control={<Checkbox checked={daily} onChange={e => setDaily(e.target.checked)} />}
              label={t('reportDaily')} />
        </Grid>
      </ReportFilter>
  );
}

const SummaryReportPage = () => {
  const classes = useStyles();
  const theme = useTheme();

  const distanceUnit = useAttributePreference('distanceUnit');
  const speedUnit = useAttributePreference('speedUnit');
  const volumeUnit = useAttributePreference('volumeUnit');

  const items = useSelector(state => state.summaryReport.items);
  const [loading, setLoading] = useState(null);

  const columns = [
    {
      headerName: t('reportDeviceName'),
      field: 'deviceName',
      type: 'string',
      width: 220,
      // valueFormatter: ({ value }) => formatPosition(value),
      renderCell: (params) => {
        return (
            <Tooltip title={formatPosition(params.value)} placement="top-start" arrow>
              <div>
                {formatPosition(params.value)}
              </div>
            </Tooltip>
        );
      },
    }, {
      headerName: t('reportStartDate'),
      field: 'startTime',
      type: 'dateTime',
      width:theme.dimensions.columnWidthDate,
      valueFormatter: ({ value }) => formatDate(value, 'YYYY-MM-DD'),
    }, {
      headerName: t('sharedDistance'),
      field: 'distance',
      type: 'number',
      width: 140,
      // width: theme.dimensions.columnWidthNumber,
      valueFormatter: ({ value }) => formatDistance(value, distanceUnit),
    }, {
      headerName: t('reportStartOdometer'),
      field: 'startOdometer',
      type: 'number',
      width: 180,
      // width: theme.dimensions.columnWidthNumber,
      valueFormatter: ({ value }) => formatDistance(value, distanceUnit),
    }, {
      headerName: t('reportEndOdometer'),
      field: 'endOdometer',
      type: 'number',
      width: 180,
      // width: theme.dimensions.columnWidthNumber,
      valueFormatter: ({ value }) => formatDistance(value, distanceUnit),
    }, {
      headerName: t('average') + ' ' + t('positionSpeed'),
      field: 'averageSpeed',
      type: 'number',
      width: 180,
      // width: theme.dimensions.columnWidthNumber,
      valueFormatter: ({ value }) => formatSpeed(value, speedUnit),
    }, {
      headerName: t('reportMaximumSpeed'),
      field: 'maxSpeed',
      type: 'number',
      width: 190,
      // width: theme.dimensions.columnWidthNumber,
      valueFormatter: ({ value }) => formatSpeed(value, speedUnit),
    }, {
      headerName: t('reportEngineHours'),
      field: 'engineHours',
      type: 'string',
      width: 180,
      // width: theme.dimensions.columnWidthNumber,
      valueFormatter: ({ value }) => formatHours(value),
    },
    {
      headerName: t('reportGHGtank_to_wheels'),
      field: 'agti',
      type: 'number',
      width: 180,
      // width: theme.dimensions.columnWidthNumber,
      valueFormatter: ({ value }) => formatAgt(value, 'kgCO2'),
    },
    {
      headerName: t('reportSpentFuel'),
      field: 'spentFuel',
      type: 'number',
      width: theme.dimensions.columnWidthNumber,
      hide: true,
      valueFormatter: ({ value }) => formatVolume(value, volumeUnit),
    }]

  return (
      <ReportLayoutPage filter={<Filter loading={loading} setLoading={setLoading} />}>
        <Paper className={classes.paper}>
          <DataGrid
              loading={loading}
              rows={items}
              columns={columns}
              // hideFooterPagination
              autoHeight
              getRowId={(row) => items.indexOf(row)}
              disableSelectionOnClick
              components={{
                LoadingOverlay: () => (
                    <GridOverlay>
                      <CircularProgress className={classes.color} />
                    </GridOverlay>
                )
              }}
          />
        </Paper>
      </ReportLayoutPage>
  );
}

export default SummaryReportPage;